import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { withFragments } from '@sorare/core/src/gql';
import { findLastIndex, sortBy } from '@sorare/core/src/lib/arrays';

import type {
  getContenderLeftFooterState_so5LeaderboardContender,
  getContenderRightFooterState_so5LeaderboardContender,
  getContenderStatus_so5LeaderboardContender,
} from './__generated__/contender.graphql';
import { hasGamesStarted, isFixtureOpened } from './so5';

export enum ContenderStatus {
  EMPTY = 'EMPTY',
  PROJECTED_DIVISION = 'PROJECTED_DIVISION',
  INCOMPLETE = 'INCOMPLETE',
  VIEW_BONUS = 'VIEW_BONUS',
  BONUS = 'BONUS',
}

const THRESHOLDS_COLORS = ['51, 206, 120', '112, 219, 198', '194, 219, 46'];

type ContenderStatusReturnType =
  | {
      status: ContenderStatus.PROJECTED_DIVISION;
      divisionChange: DivisionChange;
    }
  | { status: ContenderStatus.EMPTY }
  | { status: ContenderStatus.INCOMPLETE }
  | { status: ContenderStatus.VIEW_BONUS }
  | { status: ContenderStatus.BONUS };

export const getContenderStatus = withFragments(
  (
    contender: getContenderStatus_so5LeaderboardContender,
    isNewXpEnabled?: boolean
  ): ContenderStatusReturnType => {
    const { managerTeam, so5Leaderboard, so5Lineup } = contender;
    const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);

    const projectedLineup = managerTeam?.divisionChangingSo5Lineup;
    const projectedRanking = projectedLineup?.so5Rankings[0];

    if (isUpcoming && so5Lineup && !so5Lineup.confirmable) {
      return { status: ContenderStatus.INCOMPLETE };
    }
    if (
      isUpcoming &&
      ((isNewXpEnabled && !so5Lineup) || !isNewXpEnabled) &&
      projectedRanking?.eligibleDivisionChange
    ) {
      return {
        status: ContenderStatus.PROJECTED_DIVISION,
        divisionChange: projectedRanking?.eligibleDivisionChange,
      };
    }
    if (isUpcoming && !so5Lineup) {
      return { status: ContenderStatus.EMPTY };
    }
    if (isUpcoming && so5Lineup) {
      return { status: ContenderStatus.VIEW_BONUS };
    }

    return { status: ContenderStatus.BONUS };
  },
  {
    so5LeaderboardContender: gql`
      fragment getContenderStatus_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          so5Fixture {
            slug
            ...isFixtureOpened_so5Fixture
          }
        }
        so5Lineup {
          id
          confirmable
        }
        managerTeam {
          id
          divisionChangingSo5Lineup {
            id
            so5Rankings {
              id
              eligibleDivisionChange
            }
          }
        }
      }
      ${isFixtureOpened.fragments.so5Fixture}
    ` as TypedDocumentNode<getContenderStatus_so5LeaderboardContender>,
  }
);

export enum ContenderRightFooterState {
  LEADERBOARD = 'LEADERBOARD',
  DIVISION_CHANGE = 'DIVISION_CHANGE',
  TARGET_IN_PROGRESS = 'TARGET_IN_PROGRESS',
  TARGET_MISSED = 'TARGET_MISSED',
  TARGET_SUCCESS = 'TARGET_SUCCESS',
  THRESHOLD_IN_PROGRESS = 'THRESHOLD_IN_PROGRESS',
  THRESHOLD_MISSED = 'THRESHOLD_MISSED',
  THRESHOLD_SUCCESS = 'THRESHOLD_SUCCESS',
  POINTS_TO_NEXT_THRESHOLD = 'POINTS_TO_NEXT_THRESHOLD',
}

type ContenderRightFooterStateReturnType =
  | {
      state: ContenderRightFooterState.DIVISION_CHANGE;
      divisionChange: DivisionChange;
    }
  | { state: ContenderRightFooterState.TARGET_SUCCESS; iconUrl: string }
  | { state: ContenderRightFooterState.TARGET_IN_PROGRESS; iconUrl: string }
  | { state: ContenderRightFooterState.TARGET_MISSED }
  | {
      state: ContenderRightFooterState.THRESHOLD_IN_PROGRESS;
    }
  | {
      state: ContenderRightFooterState.POINTS_TO_NEXT_THRESHOLD;
      thresholdNumber: number;
      color: string;
      points: number;
    }
  | {
      state: ContenderRightFooterState.THRESHOLD_SUCCESS;
      thresholdNumber: number;
      color: string;
    }
  | {
      state: ContenderRightFooterState.THRESHOLD_MISSED;
    }
  | {
      state: ContenderRightFooterState.LEADERBOARD;
    }
  | null;

export const getContenderRightFooterState = withFragments(
  (
    contender: getContenderRightFooterState_so5LeaderboardContender
  ): ContenderRightFooterStateReturnType => {
    const { so5Leaderboard, so5Lineup } = contender;

    const so5Ranking = so5Lineup?.so5Rankings.at(0);
    const stillHasGamesToPlay = so5Lineup?.so5Appearances.some(
      so5Appearance => !!so5Appearance.upcomingGame
    );

    const thresholds = sortBy(
      ({ score }) => score || 0,
      (so5Leaderboard.rewardsConfig.conditional || []).filter(Boolean)
    );
    const higherTresholdReachedIndex = findLastIndex(
      thresholds,
      so5RewardConfig => {
        if (!so5RewardConfig?.score || !so5Ranking?.score) return false;
        return so5RewardConfig.score <= so5Ranking.score;
      }
    );

    if (!so5Lineup || so5Lineup?.cancelledAt) {
      return null;
    }
    if (so5Ranking?.eligibleDivisionChange) {
      return {
        state: ContenderRightFooterState.DIVISION_CHANGE,
        divisionChange: so5Ranking.eligibleDivisionChange,
      };
    }
    if (so5Ranking?.targetedRewardWithEligibility?.reward.target) {
      if (so5Ranking.targetedRewardWithEligibility.eligible) {
        return {
          state: ContenderRightFooterState.TARGET_SUCCESS,
          iconUrl:
            so5Ranking.targetedRewardWithEligibility.reward.target.pictureUrl,
        };
      }
      if (stillHasGamesToPlay) {
        return {
          state: ContenderRightFooterState.TARGET_IN_PROGRESS,
          iconUrl:
            so5Ranking.targetedRewardWithEligibility.reward.target.pictureUrl,
        };
      }
      return { state: ContenderRightFooterState.TARGET_MISSED };
    }
    if (thresholds.length > 0) {
      if (stillHasGamesToPlay && higherTresholdReachedIndex === -1) {
        return {
          state: ContenderRightFooterState.THRESHOLD_IN_PROGRESS,
        };
      }
      if (
        higherTresholdReachedIndex >= 0 &&
        higherTresholdReachedIndex < thresholds.length - 1
      ) {
        return {
          state: ContenderRightFooterState.POINTS_TO_NEXT_THRESHOLD,
          points: Math.ceil(
            (thresholds.at(higherTresholdReachedIndex + 1)?.score || 0) -
              (so5Ranking?.score || 0)
          ),
          thresholdNumber: higherTresholdReachedIndex + 2,
          color: THRESHOLDS_COLORS[higherTresholdReachedIndex],
        };
      }
      if (higherTresholdReachedIndex >= 0) {
        return {
          state: ContenderRightFooterState.THRESHOLD_SUCCESS,
          thresholdNumber: higherTresholdReachedIndex + 1,
          color: THRESHOLDS_COLORS[higherTresholdReachedIndex],
        };
      }
      return {
        state: ContenderRightFooterState.THRESHOLD_MISSED,
      };
    }

    return { state: ContenderRightFooterState.LEADERBOARD };
  },
  {
    so5LeaderboardContender: gql`
      fragment getContenderRightFooterState_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          rewardsConfig {
            conditional {
              score
            }
          }
        }
        so5Lineup {
          id
          cancelledAt
          so5Appearances {
            id
            upcomingGame {
              id
            }
          }
          so5Rankings {
            id
            score
            eligibleDivisionChange
            targetedRewardWithEligibility {
              eligible
              reward {
                target {
                  slug
                  pictureUrl
                }
              }
            }
          }
        }
      }
    ` as TypedDocumentNode<getContenderRightFooterState_so5LeaderboardContender>,
  }
);

export enum ContenderLeftFooterState {
  REWARDS = 'REWARDS',
  NOT_STARTED = 'NOT_STARTED',
  POINTS_TO_CLOSEST_REWARD = 'POINTS_TO_CLOSEST_REWARD',
  POINTS_TO_SAFETY = 'POINTS_TO_SAFETY',
  CANCELLED = 'CANCELLED',
  POINTS_TO_TARGET = 'POINTS_TO_TARGET',
  POINTS_TO_THRESHOLD = 'POINTS_TO_THRESHOLD',
}

type ContenderLeftFooterStateReturnType =
  | { state: ContenderLeftFooterState.CANCELLED }
  | { state: ContenderLeftFooterState.REWARDS }
  | { state: ContenderLeftFooterState.NOT_STARTED; date: Date }
  | { state: ContenderLeftFooterState.POINTS_TO_SAFETY; points: number }
  | {
      state: ContenderLeftFooterState.POINTS_TO_TARGET;
      points: number;
      iconUrl: string;
    }
  | {
      state: ContenderLeftFooterState.POINTS_TO_THRESHOLD;
      points: number;
      color: string;
    }
  | { state: ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD; points: number }
  | null;

export const getContenderLeftFooterState = withFragments(
  (
    contender: getContenderLeftFooterState_so5LeaderboardContender
  ): ContenderLeftFooterStateReturnType => {
    const { so5Leaderboard, so5Lineup } = contender;

    if (!so5Lineup) {
      return null;
    }
    if (so5Lineup.cancelledAt) {
      return { state: ContenderLeftFooterState.CANCELLED };
    }

    const so5Ranking = so5Lineup.so5Rankings.at(0);
    if (so5Ranking?.eligibleOrSo5Rewards.length) {
      return { state: ContenderLeftFooterState.REWARDS };
    }

    const hasLineupGamesStarted = hasGamesStarted(so5Lineup);
    if (!hasLineupGamesStarted && so5Lineup.anyEarliestGame) {
      return {
        state: ContenderLeftFooterState.NOT_STARTED,
        date: so5Lineup.anyEarliestGame.date,
      };
    }

    if (
      so5Ranking?.eligibleDivisionChange === DivisionChange.RELEGATED &&
      so5Leaderboard.divisionChangeConfiguration?.lastStableSo5Ranking
    ) {
      return {
        state: ContenderLeftFooterState.POINTS_TO_SAFETY,
        points: Math.ceil(
          so5Leaderboard.divisionChangeConfiguration.lastStableSo5Ranking
            .score - so5Ranking.score
        ),
      };
    }

    if (so5Ranking?.nextEligibleRewards) {
      const distance = Math.ceil(
        so5Ranking.nextEligibleRewards.score - so5Ranking.score
      );

      const nextTargetReward =
        so5Ranking?.nextEligibleRewards.eligibleRewards.find(
          so5RewardConfig => so5RewardConfig.target
        );
      if (nextTargetReward) {
        return {
          state: ContenderLeftFooterState.POINTS_TO_TARGET,
          points: distance,
          iconUrl: nextTargetReward.target!.pictureUrl,
        };
      }

      const isNextRewardAThreshold =
        !!so5Ranking?.nextEligibleRewards.eligibleRewards.find(
          so5RewardConfig => so5RewardConfig.score
        );
      if (isNextRewardAThreshold) {
        return {
          state: ContenderLeftFooterState.POINTS_TO_THRESHOLD,
          points: distance,
          color: THRESHOLDS_COLORS[0],
        };
      }

      return {
        state: ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD,
        points: distance,
      };
    }

    return null;
  },
  {
    so5LeaderboardContender: gql`
      fragment getContenderLeftFooterState_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          rewardsConfig {
            conditional {
              score
            }
          }
          divisionChangeConfiguration {
            id
            lastStableSo5Ranking {
              id
              score
            }
          }
        }
        so5Lineup {
          id
          cancelledAt
          anyEarliestGame {
            id
            date
          }
          so5Rankings {
            id
            score
            eligibleDivisionChange
            eligibleOrSo5Rewards {
              ... on So5RewardConfig {
                ranks
              }
              ... on So5Reward {
                slug
              }
            }
            nextEligibleRewards {
              score
              eligibleRewards {
                score
                target {
                  slug
                  pictureUrl
                }
              }
            }
            targetedRewardWithEligibility {
              eligible
            }
          }
          ...hasGamesStarted_so5Lineup
        }
      }
      ${hasGamesStarted.fragments.so5Lineup}
    ` as TypedDocumentNode<getContenderLeftFooterState_so5LeaderboardContender>,
  }
);
